import { ref } from "vue";
import { defineStore } from "pinia";
import { Organisation, Form } from "@/helpers/interfaces/organisations";

/**
 * Store for managing organisation-specific state, such as the organisation's shortcode, form name, organisation details, and form structure. This store is designed
 * to be used across the checkout process to maintain consistency of organisation-related data and facilitate communication between different components and providers.
 * The store includes functions to set and update this information, and it persists the relevant data in session storage to maintain state across page reloads within the same session.
 * The form name is determined based on the type of payment method, with a default value of "invoice" if the type is undefined or empty. This allows for flexibility in handling different
 * types of payment methods while ensuring that there is always a valid form name available for use in the checkout process.
 */
export const useOrganisationStore = defineStore(
    "organisation",
    () => {
        const shortcode = ref<string | null>(null);
        const formName = ref<string | null>(null);
        const organisation = ref<Organisation | null>(null);
        const formStructure = ref<Form | null>(null);

        function setShortcode(value: string | null) {
            shortcode.value = value;
        }

        function setFormName(type: string | null) {
            if (type === "undefined") {
                formName.value = "invoice";
            }

            if (type !== "undefined") {
                if (type !== "") formName.value = type;
                else formName.value = "invoice";
            }
        }

        function setOrganisation(org: Organisation | null) {
            organisation.value = org;
        }

        function setFormStructure(form: Form | null) {
            formStructure.value = form;
        }

        return {
            shortcode,
            formName,
            organisation,
            formStructure,
            setShortcode,
            setFormName,
            setOrganisation,
            setFormStructure,
        };
    },
    {
        persist: {
            pick: ["shortcode", "formName", "organisation", "formStructure"],
            storage: sessionStorage,
        },
    },
);
